import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { ComponentData, ComponentRenderMode } from '../../../types/component-skin';
import { TrendingItem } from './TrendingItems.types';
import { useTranslation } from '../../../redux/TranslationHelper';

/**
 * TrendingItems 컴포넌트 로직 훅
 * 트렌딩 아이템 표시와 관련된 모든 비즈니스 로직을 처리
 */
export function useTrendingItemsLogic(componentData: ComponentData, mode: ComponentRenderMode) {
  const { t } = useTranslation();
  
  // props에서 필요한 데이터 추출
  const {
    id,
    style = {},
    className = '',
    componentProps = {},
    data = {},
    props = {}
  } = componentData;
  

  // componentProps가 빈 객체인지 확인
  const hasComponentProps = componentProps && Object.keys(componentProps).length > 0;

  // 컴포넌트 속성 추출
  // componentProps에서 직접 추출, 기본값 사용
  const {
    title = "Trending now",  // t() 제거하여 기본값이 제대로 적용되는지 확인
    subtitle = "에디터가 선정한",  // t() 제거하여 기본값이 제대로 적용되는지 확인
    is_logged = true,
    is_not_logged = true,
    imageRadius = "4px",
    showMoreButton = true,
    moreButtonText = "MORE",
    moreButtonUrl = "#",
    moreButtonTextColor = "#8a7a6d",
    moreButtonBorderColor = "#8a7a6d",
    moreButtonBgColor = "transparent",
    moreButtonHoverTextColor = "#ffffff",
    moreButtonHoverBgColor = "#8a7a6d",
    items = []
  } = componentProps;
  

  
  // 로컬 상태
  const [hoveredItemId, setHoveredItemId] = useState<string | number | null>(null);
  const [isMoreButtonHovered, setIsMoreButtonHovered] = useState(false);
  
  // 컴포넌트 고유 ID
  const componentUniqueId = useRef(`trending-items-${id || Math.random().toString(36).substr(2, 9)}`);
  
  // 번역 함수 메모이제이션
  const translate = useCallback((text: string) => t(text), [t]);
  
  // 호버 스타일 동적 생성 및 적용
  useEffect(() => {
    // 현재 ID를 변수에 저장
    const currentId = componentUniqueId.current;
    
    // 기존 스타일 태그 제거
    const existingStyle = document.getElementById(`hover-style-${currentId}`);
    if (existingStyle) {
      existingStyle.remove();
    }
    
    // 새 스타일 태그 생성
    const styleEl = document.createElement('style');
    styleEl.id = `hover-style-${currentId}`;
    styleEl.innerHTML = `
      .${currentId} .trending-items-more-button:hover {
        background-color: ${moreButtonHoverBgColor || '#8a7a6d'} !important;
        color: ${moreButtonHoverTextColor || '#ffffff'} !important;
      }
    `;
    document.head.appendChild(styleEl);
    
    // 컴포넌트 언마운트 시 스타일 태그 제거
    return () => {
      const styleToRemove = document.getElementById(`hover-style-${currentId}`);
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, [moreButtonHoverBgColor, moreButtonHoverTextColor]);
  
  // URL 유효성 검사 함수
  const isValidUrl = useCallback((url: string | undefined | null): boolean => {
    return !!url && url !== "#" && url.trim() !== "";
  }, []);
  
  // MORE 버튼 URL 유효성
  const hasValidMoreUrl = useMemo(() => isValidUrl(moreButtonUrl), [moreButtonUrl, isValidUrl]);
  
  // 버튼 스타일 계산
  const moreButtonStyle = useMemo(() => ({
    color: moreButtonTextColor || "#8a7a6d",
    borderColor: moreButtonBorderColor || "#8a7a6d",
    backgroundColor: moreButtonBgColor || "transparent"
  }), [moreButtonTextColor, moreButtonBorderColor, moreButtonBgColor]);
  
  // 번역된 텍스트
  const translatedTexts = useMemo(() => {

    
    const result = {
      moreButtonText: moreButtonText ? translate(moreButtonText) : '',
      title: title ? translate(title) : '',
      subtitle: subtitle ? translate(subtitle) : ''
    };
    

    return result;
  }, [moreButtonText, title, subtitle, translate]);
  
  // 최종 클래스명
  const finalClassName = `${className} ${componentUniqueId.current}`;
  
  // 액션 핸들러
  const handleItemClick = useCallback((item: TrendingItem, e: React.MouseEvent) => {
    // 에디터 모드에서만 기본 동작 방지
    if (mode === 'editor') {
      e.preventDefault();
      return;
    }
    
    // preview와 production 모드에서는 유효한 URL이 있으면 이동
    if (isValidUrl(item.url)) {
      // 명시적으로 window.location.href로 이동
      window.location.href = item.url;
      e.preventDefault(); // 기본 <a> 태그 동작 방지하고 직접 이동
    } else {
      e.preventDefault();
    }
  }, [mode, isValidUrl]);
  
  const handleMoreButtonClick = useCallback((e: React.MouseEvent) => {
    // 에디터 모드에서만 기본 동작 방지
    if (mode === 'editor') {
      e.preventDefault();
      return;
    }
    
    // preview와 production 모드에서는 유효한 URL이 있으면 이동
    if (hasValidMoreUrl) {
      // 명시적으로 window.location.href로 이동
      window.location.href = moreButtonUrl;
      e.preventDefault(); // 기본 <a> 태그 동작 방지하고 직접 이동
    } else {
      e.preventDefault();
    }
  }, [mode, hasValidMoreUrl, moreButtonUrl]);
  
  const handleItemHover = useCallback((itemId: string | number | null) => {
    setHoveredItemId(itemId);
  }, []);
  
  const handleMoreButtonHover = useCallback((isHovered: boolean) => {
    setIsMoreButtonHovered(isHovered);
  }, []);
  
  // 데이터와 액션 반환
  return {
    data: {
      // 설정값
      id,
      style,
      className: finalClassName,
      title: translatedTexts.title,
      subtitle: translatedTexts.subtitle,
      is_logged,
      is_not_logged,
      imageRadius,
      showMoreButton,
      moreButtonText: translatedTexts.moreButtonText,
      moreButtonUrl,
      moreButtonStyle,
      items,
      // 상태값
      hoveredItemId,
      isMoreButtonHovered,
      hasValidMoreUrl,
      // 유틸리티
      componentUniqueId: componentUniqueId.current,
      mode
    },
    actions: {
      handleItemClick,
      handleMoreButtonClick,
      handleItemHover,
      handleMoreButtonHover,
      isValidUrl,
      translate
    }
  };
}